import { createContext, useContext, useMemo, useReducer } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { CalendarEventDateString } from 'components/calendar';

import type { Course } from 'shared-types';
import type { GetPathsMinimalRes } from 'shared-types/responses';
import type { GetCalendarEventsMinimalRes } from 'shared-types/responses';

interface HomePageSettingsContext {
  courseOptions: { label: string; value: number }[];
  eventOptions: { label: string; value: number }[];
  pathOptions: { label: string; value: number }[];
  errors: { [key: string]: boolean };
}

type ActionType = 'updateErrors';

type Action = unknown & {
  type: ActionType;
  value: unknown;
};

const reducer = (state: HomePageSettingsContext, action: Action) => {
  if (action.type === 'updateErrors') {
    return {
      ...state,
      errors: {
        ...state.errors,
        ...(action.value as HomePageSettingsContext['errors'])
      }
    };
  }

  return state;
};

const initialState: HomePageSettingsContext = {
  courseOptions: [],
  eventOptions: [],
  pathOptions: [],
  errors: {}
};

const HomePageSettingsContext =
  createContext<HomePageSettingsContext>(initialState);

const HomePageSettingsDispatchContext = createContext<React.Dispatch<Action>>(
  () => {}
);

interface HomePageSettingsContextProviderProps
  extends React.PropsWithChildren<{}> {}

const HomePageSettingsContextProvider = ({
  children
}: HomePageSettingsContextProviderProps) => {
  const { theme } = useReduxState();

  const [state, dispatch] = useReducer(reducer, initialState);

  const { data: courseOptionsData } = useApiData<{
    courses: Pick<Course, 'id' | 'slug' | 'title'>[];
    success: true;
  }>({ url: '/admin/courses-administered' });

  const courseOptions = useMemo(() => {
    return (
      courseOptionsData?.courses.map((course) => ({
        label: course.title,
        value: course.id
      })) || []
    );
  }, [courseOptionsData]);

  const { data: pathOptionsData } = useApiData<GetPathsMinimalRes>({
    url: '/admin/paths?onlyPathData=true'
  });

  const pathOptions = useMemo(() => {
    return (
      pathOptionsData?.paths.map((path) => ({
        label: path.title,
        value: path.id
      })) || []
    );
  }, [pathOptionsData]);

  const { data: eventOptionsData } = useApiData<GetCalendarEventsMinimalRes>({
    url: '/admin/calendar-events?minimal=true&sortBy=newest'
  });

  const eventOptions = useMemo(() => {
    return (
      eventOptionsData?.events.map((event) => ({
        helpText: (
          <div
            style={{
              color:
                new Date(event.endDate) >= new Date()
                  ? theme.textColorLighter
                  : theme.dangerColor
            }}
          >
            <CalendarEventDateString
              startDate={event.startDate as string}
              endDate={event.endDate as string}
            />
          </div>
        ),
        label: event.title,
        value: event.id
      })) || []
    );
  }, [eventOptionsData, theme]);

  const contextValue = useMemo(() => {
    return { ...state, courseOptions, pathOptions, eventOptions };
  }, [state, courseOptions, pathOptions, eventOptions]);

  return (
    <HomePageSettingsContext.Provider value={contextValue}>
      <HomePageSettingsDispatchContext.Provider value={dispatch}>
        {children}
      </HomePageSettingsDispatchContext.Provider>
    </HomePageSettingsContext.Provider>
  );
};

const useHomePageSettingsContext = () => {
  const state = useContext(HomePageSettingsContext);
  return state;
};

const useHomePageSettingsDispatch = () => {
  const dispatch = useContext(HomePageSettingsDispatchContext);
  return dispatch;
};

export {
  HomePageSettingsContextProvider,
  useHomePageSettingsContext,
  useHomePageSettingsDispatch
};
